import { Component, ViewChild, computed, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { AppMatAutocompleteComponent, XmedicalUiModule } from '@xmedical/ui';
import { DiagnosisCode, PipesModule } from '@xmedical/core';
import { WithReferralInfo, hasReferralInfo, formatReferralSource } from '../../types/referral-info';

/**
 * Generic interface for displayed diagnosis items.
 * Can be either a DiagnosisCode or a PatientDiagnosisCode.
 */
export interface DiagnosisDisplayItem extends WithReferralInfo {
  id?: number;
  code: string;
  description?: string;
  comment?: string;
  lastModifiedDate?: Date;
  modifiedDateUtc?: Date;
  lastModifiedUser?: { firstName?: string; surname?: string };
}

/**
 * Event emitted when a diagnosis is added from search results.
 */
export interface DiagnosisAddedEvent {
  item: DiagnosisCode;
  comment: string;
}

/**
 * Event emitted when a diagnosis is removed from the list.
 */
export interface DiagnosisRemovedEvent {
  item: DiagnosisDisplayItem;
  index: number;
}

@Component({
  selector: 'xm-diagnosis-widget-view',
  templateUrl: './diagnosis-widget-view.component.html',
  styleUrls: ['./diagnosis-widget-view.component.scss'],
  imports: [
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    XmedicalUiModule,
    PipesModule
  ]
})
export class DiagnosisWidgetViewComponent {
  @ViewChild(AppMatAutocompleteComponent) autocomplete!: AppMatAutocompleteComponent;

  // Inputs
  items = input<DiagnosisDisplayItem[]>([]);
  title = input<string>('Diagnosis Codes');
  searchFn = input.required<(query: string) => Observable<DiagnosisCode[]>>();
  isProcessing = input<boolean>(false);
  canAdd = input<boolean>(true);
  canDelete = input<boolean>(true);
  usePrimaryText = input<boolean>(false);

  // Outputs
  itemAdded = output<DiagnosisAddedEvent>();
  itemRemoved = output<DiagnosisRemovedEvent>();

  // Track comments for search results before adding
  resultComments: { [key: number]: string } = {};

  // Computed signal for reactive hasItems check
  hasItems = computed(() => this.items().length > 0);

  getResultComment(itemId: number): string {
    return this.resultComments[itemId] || '';
  }

  setResultComment(itemId: number, comment: string): void {
    this.resultComments[itemId] = comment;
  }

  onItemSelected(result: DiagnosisCode): void {
    const comment = this.getResultComment(result.id);
    this.itemAdded.emit({ item: result, comment });
    this.clearSearch();
  }

  onRemoveItem(item: DiagnosisDisplayItem, index: number): void {
    this.itemRemoved.emit({ item, index });
  }

  private clearSearch(): void {
    this.resultComments = {};
    if (this.autocomplete) {
      this.autocomplete.cancelSearch();
    }
  }

  // Referral helper methods for template
  isReferred(item: DiagnosisDisplayItem): boolean {
    return hasReferralInfo(item);
  }

  getReferralText(item: DiagnosisDisplayItem): string {
    return formatReferralSource(item);
  }
}
